import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdCheckboxGroup,
  CmdChipSelect,
  CmdInput,
  CmdLabel,
  CmdRadioButton,
  CmdSimpleSelect,
  CmdSubpage,
  CmdSubpageButton,
  CmdSwitch,
} from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../Services/FormikValidatorService';
import PermissionsService, { PERMISSION_AUTO_REPORT } from '../../Services/PermissionsService';
import { reportSettingsOptionsR08 } from './ReportSettings';
import { IOption } from '../Forms/CmdField';
import usePageWithForm from '../../hooks/usePageWithForm';
import OnlineHelp from '../OnlineHelp';
import { IAutoReportFormProps, R08Form } from './types';
import {
  getBluecoins,
  getFrequency,
  getFrequencyParamDaysInMonth,
  getFrequencyParamDaysInWeek,
  getFrequencyParamMonths,
  getReportlabel,
  getValidation,
  handleReportSettings,
} from './AutoReportService';
import { transformErrors } from '../../Services/HookFormService';
import useAnalytics from '../../hooks/useAnalytics';

export default function ReportR08Form(props: IAutoReportFormProps): JSX.Element {
  usePageWithForm();
  const intlShape = useIntl();
  const { formatMessage: f } = intlShape;
  const [loader, setLoader] = React.useState<boolean>(false);
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const [bluecoinOptions, setBluecoinOptions] = React.useState<IOption[]>([]);
  const [frequecyOptions, setFrequecyOptions] = React.useState<IOption[]>([]);
  const { trackEvent } = useAnalytics();

  const validateFields = (values: R08Form) => {
    let formErrors = {};
    if (validation) {
      formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
    }
    return transformErrors(formErrors);
  };

  const getbluecoinIds = () => {
    if (props.editingData && props.editingData.bluecoins) {
      return Object.values(props.editingData.bluecoins).map((i) => String(i));
    }
    return [];
  };

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm<R08Form>({
    mode: 'onChange',
    defaultValues: {
      active: props.editingData ? (props.editingData.active ? 1 : 0) : props.isActive,
      frequency: props.editingData ? props.editingData.frequency : '',
      name: props.editingData ? props.editingData.name : props.reportName,
      reportId: props.selectedReportId,
      format: 'xlsx',
      bluecoinIds: getbluecoinIds(),
      reportSettings: props.editingData
        ? handleReportSettings(props.editingData.reportSettings, reportSettingsOptionsR08)
        : [],
      frequencyParamDaysInWeek: props.editingData ? String(props.editingData.frequencyParam) : '1',
      frequencyParamDaysInMonth: props.editingData ? String(props.editingData.frequencyParam) : '1',
      frequencyParamMonths: props.editingData ? String(props.editingData.frequencyParam) : '1',
      excludeRecordsShorterThan: props.editingData
        ? props.editingData?.reportSettings?.excludeRecordsShorterThan
        : 5,
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const getBluecoinsData = async () => {
    const options: IOption[] | false = await getBluecoins(props.selectedReportId);
    if (options) {
      setBluecoinOptions(options);
    }
  };

  const onSubmit = (values: R08Form) => {
    trackEvent(['auto reports', 'click', 'Autoreports R08 save']);
    props.saveReport(values, reportSettingsOptionsR08);
  };

  const subpageButtons: CmdSubpageButton[] =
    (props.editingData &&
      PermissionsService.hasAccess(`${PERMISSION_AUTO_REPORT}0${props.selectedReportId}`, [
        'update',
      ])) ||
    !props.editingData
      ? [
          {
            id: 'autoreport-form-close',
            type: 'button',
            className: 'e-button',
            title: f({ id: 'suppliersForm.close' }),
            closeCallback: props.closeCallback,
          },
          {
            id: 'autoreport-form-submit',
            type: 'submit',
            className: 'e-button e-button--gray',
            title: f({ id: 'suppliersForm.save' }),
            submitCallback: handleSubmit(onSubmit),
          },
        ]
      : [
          {
            id: 'autoreport-form-close',
            type: 'button',
            className: 'e-button',
            title: f({ id: 'suppliersForm.close' }),
            closeCallback: props.closeCallback,
          },
        ];

  const getReportValidation = async () => {
    const responseData = await getValidation(props.selectedReportId);
    if (responseData) {
      setValidation(responseData.form);
      if (!props.editingData) {
        setValue(
          'reportSettings',
          handleReportSettings(responseData?.userSettings?.value, reportSettingsOptionsR08)
        );
      }
    } else if (!props.editingData) {
      setValue(
        'reportSettings',
        handleReportSettings([], reportSettingsOptionsR08)
      );
    }
  };

  const handleFrequency = async () => {
    setLoader(true);
    const response = await getFrequency(props.selectedReportId);
    if (response) {
      setFrequecyOptions(response);
    }
    setLoader(false);
  };

  const getReportSettingsOptions = () => {
    return reportSettingsOptionsR08.map((item: string) => {
      return { value: item, item: f({ id: `reports.R08.${item}` }) };
    });
  };

  useQueries({
    queries: [
      {
        queryKey: ['autoReportR08Validation', props.selectedReportId],
        queryFn: () => getReportValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['autoReportR08Customers', props.selectedReportId],
        queryFn: () => getBluecoinsData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['autoReportR08Frequency', props.selectedReportId],
        queryFn: () => handleFrequency(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  return (
    <CmdSubpage
      id="autoreports-form"
      title={f({ id: 'menu.autoReports' })}
      subtitle={f({ id: props.editingData ? 'table.detail' : 'table.add' })}
      buttons={subpageButtons}
      loader={loader}
      isNavigatorEnabled={false}
      maintitle={f({ id: 'menu.reports' })}
      onlineHelp={<OnlineHelp topic="add-auto-report" />}
    >
      <div>
        <div className="row">
          <div className="col-xxl-6">
            <CmdBox
              id="alarms-basic-information"
              title={f({ id: 'alarms.section.basicInformation' })}
            >
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <CmdInput
                    {...field}
                    id="name"
                    label={f({ id: 'form.title' })}
                    type="text"
                    required={true}
                    onChangeCallback={(value) => {
                      setValue('name', value);
                      trigger('name');
                    }}
                    error={errors.name?.message}
                    touched={true}
                  />
                )}
              />

              {props.editingData ? (
                <CmdLabel
                  title={f({ id: 'reports.reportId' })}
                  value={getReportlabel(props.selectedReportId, props.reportsList)}
                />
              ) : (
                <Controller
                  name="reportId"
                  control={control}
                  rules={{ required: errors.reportId ? errors.reportId.message : undefined }}
                  render={({ field }) => (
                    <CmdSimpleSelect
                      {...field}
                      name="reportId"
                      id="reportId"
                      value={props.selectedReportId}
                      label={f({ id: 'reports.reportId' })}
                      introOption={f({ id: 'suppliersForm.choose' })}
                      options={props.reportsList}
                      required={true}
                      onChangeCallback={(value: string) => {
                        setValue('reportId', value);
                        props.handleReportId(value, watch('name'), watch('active'));
                        trackEvent(['auto reports', 'change', 'Autoreports R08 reportId change']);
                      }}
                      error={errors.reportId?.message}
                      touched={true}
                    />
                  )}
                />
              )}
            </CmdBox>
            <CmdBox
              id="reports-sending-settings"
              title={f({ id: 'reports.section.sendingSettings' })}
            >
              <Controller
                name="active"
                control={control}
                rules={{
                  required: errors.active ? errors.active.message : undefined,
                }}
                render={({ field }) => (
                  <CmdSwitch
                    {...field}
                    id="active"
                    label={f({ id: 'suppliersForm.active' })}
                    value={watch('active')}
                    setValue={(value: number) => {
                      setValue('active', value);
                      trigger('active');
                      trackEvent(['auto reports', 'change', 'Autoreports R08 active change']);
                    }}
                  />
                )}
              />

              <Controller
                name="frequency"
                control={control}
                rules={{ required: errors.frequency ? errors.frequency.message : undefined }}
                render={({ field }) => (
                  <CmdSimpleSelect
                    {...field}
                    name="frequency"
                    id="frequency"
                    label={f({ id: 'reports.frequency' })}
                    introOption={f({ id: 'reports.chooseFrequency' })}
                    options={frequecyOptions}
                    required={true}
                    error={errors.frequency?.message}
                    touched={true}
                    onChangeCallback={(value) => {
                      setValue('frequency', value);
                      trigger('frequency');
                      trackEvent(['auto reports', 'change', 'Autoreports R08 frequency change']);
                    }}
                  />
                )}
              />

              {watch('frequency') === 'weekly' && (
                <Controller
                  name="frequencyParamDaysInWeek"
                  control={control}
                  rules={{
                    required: errors.frequencyParamDaysInWeek
                      ? errors.frequencyParamDaysInWeek.message
                      : undefined,
                  }}
                  render={({ field }) => (
                    <CmdSimpleSelect
                      {...field}
                      name="frequencyParamDaysInWeek"
                      id="frequencyParamDaysInWeek"
                      label={f({ id: 'reports.frequencyParamDaysInWeek' })}
                      options={getFrequencyParamDaysInWeek(intlShape)}
                      required={true}
                      error={errors.frequencyParamDaysInWeek?.message}
                      touched={true}
                      value={watch('frequencyParamDaysInWeek')}
                      onChangeCallback={(value) => {
                        setValue('frequencyParamDaysInWeek', value);
                        trigger('frequencyParamDaysInWeek');
                        trackEvent([
                          'auto reports',
                          'change',
                          'Autoreports R08 frequencyParamDaysInWeek change',
                        ]);
                      }}
                    />
                  )}
                />
              )}
              {watch('frequency') === 'monthly' && (
                <Controller
                  name="frequencyParamDaysInMonth"
                  control={control}
                  rules={{
                    required: errors.frequencyParamDaysInMonth
                      ? errors.frequencyParamDaysInMonth.message
                      : undefined,
                  }}
                  render={({ field }) => (
                    <CmdSimpleSelect
                      {...field}
                      name="frequencyParamDaysInMonth"
                      id="frequencyParamDaysInMonth"
                      label={f({ id: 'reports.frequencyParamDaysInMonth' })}
                      options={getFrequencyParamDaysInMonth(intlShape)}
                      required={true}
                      error={errors.frequencyParamDaysInMonth?.message}
                      touched={true}
                      onChangeCallback={(value) => {
                        setValue('frequencyParamDaysInMonth', value);
                        trigger('frequencyParamDaysInMonth');
                        trackEvent([
                          'auto reports',
                          'change',
                          'Autoreports R08 frequencyParamDaysInMonth change',
                        ]);
                      }}
                    />
                  )}
                />
              )}

              {watch('frequency') === 'yearly' && (
                <Controller
                  name="frequencyParamMonths"
                  control={control}
                  rules={{
                    required: errors.frequencyParamMonths
                      ? errors.frequencyParamMonths.message
                      : undefined,
                  }}
                  render={({ field }) => (
                    <CmdSimpleSelect
                      {...field}
                      name="frequencyParamMonths"
                      id="frequencyParamMonths"
                      label={f({ id: 'reports.frequencyParamMonths' })}
                      options={getFrequencyParamMonths(intlShape)}
                      required={true}
                      error={errors.frequencyParamMonths?.message}
                      touched={true}
                      onChangeCallback={(value) => {
                        setValue('frequencyParamMonths', value);
                        trigger('frequencyParamMonths');
                        trackEvent([
                          'auto reports',
                          'change',
                          'Autoreports R08 frequencyParamMonths change',
                        ]);
                      }}
                    />
                  )}
                />
              )}

              <Controller
                name="format"
                control={control}
                render={({ field }) => (
                  <CmdRadioButton
                    {...field}
                    id="format"
                    name="format"
                    label={f({ id: 'reports.format' })}
                    options={[{ item: '.xlsx', value: 'xlsx' }]}
                    required={true}
                    disabled={true}
                  />
                )}
              />

              <Controller
                name="bluecoinIds"
                control={control}
                rules={{
                  required: errors.bluecoinIds ? errors.bluecoinIds.message : undefined,
                }}
                render={({ field }) => (
                  <CmdChipSelect
                    {...field}
                    id="bluecoinIds"
                    name="bluecoinIds"
                    label={f({ id: 'menu.bluecoins' })}
                    options={bluecoinOptions}
                    required={true}
                    placeholder={f({ id: 'form.choose' })}
                    searchPlaceholder={f({ id: 'form.search' })}
                    selectButton={f({ id: 'form.groupChipSelectUnique.button.apply' })}
                    resetButton={f({ id: 'form.groupChipSelectUnique.button.reset' })}
                    searchTooltip={f({
                      id: 'alarms.section.vehicles.driverId.search.tooltip',
                    })}
                    selectButtonTooltip={f({
                      id: 'alarms.section.vehicles.driverId.selectButton.tooltip',
                    })}
                    resetButtonTooltip={f({
                      id: 'alarms.section.vehicles.driverId.resetButton.tooltip',
                    })}
                    bossCheckbox={f({ id: 'alarms.modal.timeFrame.button.selectAll' })}
                    error={errors.bluecoinIds?.message}
                    touched={true}
                    value={watch('bluecoinIds')}
                    setValue={(value: string[]) => {
                      setValue('bluecoinIds', value);
                      trigger('bluecoinIds');
                      trackEvent(['auto reports', 'change', 'Autoreports R08 bluecoinIds change']);
                    }}
                  />
                )}
              />

              <Controller
                name="excludeRecordsShorterThan"
                control={control}
                rules={{
                  required: errors.excludeRecordsShorterThan
                    ? errors.excludeRecordsShorterThan.message
                    : undefined,
                }}
                render={({ field }) => (
                  <CmdInput
                    {...field}
                    id="excludeRecordsShorterThan"
                    name="excludeRecordsShorterThan"
                    label={f({ id: 'reports.excludeRecordsShorterThan' })}
                    currency="min"
                    value={watch('excludeRecordsShorterThan')}
                    type="text"
                    onChangeCallback={(value: number) => {
                      setValue('excludeRecordsShorterThan', value);
                    }}
                    error={errors.excludeRecordsShorterThan?.message}
                    touched={true}
                  />
                )}
              />
            </CmdBox>
          </div>

          <div className="col-xxl-6">
            <CmdBox id="reports-sending-settings" title={f({ id: 'reports.reportSettings' })}>
              {reportSettingsOptionsR08.length > 0 ? (
                <Controller
                  name="reportSettings"
                  control={control}
                  rules={{
                    required: errors.reportSettings ? errors.reportSettings.message : undefined,
                  }}
                  render={({ field }) => (
                    <CmdCheckboxGroup
                      {...field}
                      key="reportSettings"
                      id="reportSettings"
                      name="reportSettings"
                      value={watch('reportSettings')}
                      label={f({ id: 'reports.parametersSetting' })}
                      options={getReportSettingsOptions()}
                      gridTemplateColumns=" 1fr 1fr 1fr "
                      bossCheckbox={f({ id: 'reports.chooseAll' })}
                      required={false}
                      setValue={(value: string[]) => {
                        setValue('reportSettings', value);
                        trigger('reportSettings');
                        trackEvent([
                          'auto reports',
                          'change',
                          'Autoreports R08 reportSettings change',
                        ]);
                      }}
                    />
                  )}
                />
              ) : (
                <CmdLabel
                  title={f({ id: 'reports.parametersSetting' })}
                  value={f({ id: 'reports.noParametersAvailable' })}
                  isLabelInTwoRows={true}
                />
              )}
            </CmdBox>

            {props.recipientsBox}
          </div>
        </div>
      </div>
    </CmdSubpage>
  );
}
